import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useAds } from "@/store/ads";

const KEY = "bb_exit_intent_v1";

export default function ExitIntentModal() {
  const { adMode } = useAds();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | done

  useEffect(() => {
    if (adMode) return; // don't nag paid traffic on exit
    if (localStorage.getItem(KEY)) return;
    let armed = false;
    // Arm after 6s so it doesn't fire immediately
    const armTimer = setTimeout(() => (armed = true), 6000);
    const handler = (e) => {
      if (!armed) return;
      if (e.clientY <= 0) {
        setOpen(true);
        localStorage.setItem(KEY, "seen");
        document.removeEventListener("mouseleave", handler);
      }
    };
    document.addEventListener("mouseleave", handler);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", handler);
    };
  }, [adMode]);

  const submit = async (e) => {
    e.preventDefault();
    setState("submitting");
    try {
      await api.post("/newsletter/subscribe", { email, source: "exit_intent" });
      setState("done");
      setTimeout(() => setOpen(false), 2000);
    } catch {
      setState("idle");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center px-6"
          data-testid="exit-intent-modal"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-2xl border border-[#A855F7]/40 bg-[#0a0a0a] p-10 shadow-[0_0_60px_rgba(168,85,247,0.4)]"
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white"
              data-testid="exit-intent-close"
            >
              <X size={20} />
            </button>
            <Mail className="text-[#A855F7]" size={22} />
            <h2 className="font-display text-3xl text-white mt-4">
              Leaving so soon?
            </h2>
            <p className="text-white/60 mt-2 text-sm">
              Drop your email and we'll send you drops, restocks, and the occasional cheeky discount.
            </p>
            {state === "done" ? (
              <div className="mt-6 text-[#FF3D8B] text-sm" data-testid="exit-intent-thanks">
                You're in. Check your inbox.
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@somewhere.com"
                  data-testid="exit-intent-email"
                  className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#A855F7]"
                />
                <button
                  type="submit"
                  disabled={state === "submitting"}
                  data-testid="exit-intent-submit"
                  className="px-5 py-2.5 rounded-full bg-[#A855F7] text-black font-bold uppercase tracking-widest text-xs hover:bg-[#b874ff] transition-colors disabled:opacity-40"
                >
                  Join
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
